import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Slider from "react-slick";
import { calculateDiscountPrice, formatImageName, formatPrice, handleError } from "../utils/functions";
import axiosInstance from "../config/axios.config";
import { toast } from "react-toastify";
import Loading from "./Refine/Loading";
import Rating from "./Refine/Rating";
import NewArrivalTwo from "./NewArrivalTwo";
import { useDispatch, useSelector } from "react-redux";
import { updateCart } from "../store/slice/cart";
import useCurrency from "../hooks/useCurrency";
import timeAgo from "../utils/timeago";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react/dist/iconify.js";
const settingsThumbs = {
  dots: false,
  infinite: true,
  speed: 500,
  slidesToShow: 4,
  slidesToScroll: 1,
  focusOnSelect: true,
};

const ProductDetailsTwo = () => {
  const navigate = useNavigate();
  const { token } = useSelector((state) => state.auth);
  const [currency] = useCurrency();

  const dispatch = useDispatch();
  const { slug } = useParams();
  const [offer, setOffer] = useState("");
  const [selected, setSelected] = useState({
    quantity: 1,
    size: "",
    color: "",
  });

  const [data, setData] = useState({});
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [mainImage, setMainImage] = useState(null);

  const { t } = useTranslation();

  const getData = useCallback(
    async (Loading = true) => {
      try {
        setIsLoading(Loading);
        const { data } = await axiosInstance.get(`/product/get/${slug}`);
        if (!data.error) {
          setMainImage(formatImageName(data.data.product?.images[0] || ""));
          setData({
            ...data.data.product,
            offers: data.data.product.offers || [], // Ensure offers is an array
          });
          setRelated(data.data.related);
          setReviews(data.data.reviews);
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoading(false);
      }
    },
    [slug]
  );
  useEffect(() => {
    getData();
  }, [getData]);

  // increment & decrement
  const incrementQuantity = () => setSelected((prev) => ({ ...prev, quantity: prev.quantity + 1 }));
  const decrementQuantity = () =>
    setSelected((prev) => ({ ...prev, quantity: prev.quantity > 1 ? prev.quantity - 1 : prev.quantity }));

  const addToCart = async () => {
    if (!token) {
      toast.error("Please Login to add Item to Cart");
      return;
    }

    if (data.sizes?.length > 0 && !selected.size) {
      toast.error("Please select a size.");
      return;
    }
    if (data.colors?.length > 0 && !selected.color) {
      toast.error("Please select a color.");
      return;
    }

    try {
      const { data: res } = await axiosInstance.post("/cart", {
        productId: data._id,
        quantity: selected.quantity,
        size: selected.size,
        color: selected.color,
      });

      if (!res.error) {
        toast.success(res.message);
        dispatch(updateCart({ cart: res.cart, totalQuantity: res.cart.length }));
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const submitOffer = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Please Login to Submit the Offer");
      return;
    }
    try {
      const { data } = await axiosInstance.post("/offer", { slug, price: offer });
      if (!data.error) {
        toast.success(data.message);
        setData((prev) => ({ ...prev, offers: [...prev.offers, data.newOffer] }));
      } else {
        toast.error(data.message);
      }
      setOffer("");
    } catch (error) {
      handleError(error);
    }
  };

  const getAcceptedOfferPrice = (offers = []) => {
    const acceptedOffer = offers.find((offer) => offer.status === "ACCEPTED");
  
    const counteredAccepted = offers.find(
      (offer) => offer.status === "COUNTERED" && offer.counterOffer.status === "ACCEPTED"
    );
  
    return acceptedOffer?.price || counteredAccepted?.counterOffer?.price || null;
  };

  const handleInitiateChat = async () => {
    try {
      if (!token) {
        toast.error("Please Login to initiate chat");
        return;
      }
      const { data: res } = await axiosInstance.post("/user/chat/create", {
        shopId: data?.shopId._id,
      });
      if (!res.error) {
        navigate(`/dashboard/chat?chatId=${res.data}`);
        toast.success(res.message);
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const buyNow = async () => {
    if (!token) {
      toast.error("Please Login to Buy Product");
      return;
    }

    if (data.sizes?.length > 0 && !selected.size) {
      toast.error("Please select a size.");
      return;
    }
    if (data.colors?.length > 0 && !selected.color) {
      toast.error("Please select a color.");
      return;
    }

    try {
      const { data: res } = await axiosInstance.post("/cart/buy-now", {
        productId: data._id,
        quantity: selected.quantity,
        size: selected.size,
        color: selected.color,
      });

      if (!res.error) {
        navigate("/checkout");
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const acceptOffer = async (id, status) => {
    try {
      const { data } = await axiosInstance.put(`/offer/${id}/status`, { status });
      if (!data.error) {
        toast.success(data.message);
        getData(false);
      }
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <Loading isLoading={isLoading}>
      <section className="product-details bg-white py-80">
        <div className="container container-lg">
          <div className="row gy-4">
            <div className="col-xl-9">
              <div className="row gy-4">
                <div className="col-xl-6">
                  <div className="product-details__left">
                    <div className="customImageMain border border-gray-100 rounded-16">
                      <img src={mainImage} alt="Main Product" className="p-20" />
                    </div>
                    <div className="mt-56">
                      <div className="product-details__images-slider">
                        <Slider {...settingsThumbs}>
                          {data?.images?.map((image, index) => (
                            <div
                              className="center max-w-120 max-h-120 h-100 flex-center border border-gray-100 rounded-16 p-8"
                              key={index}
                              onClick={() => setMainImage(formatImageName(image))}
                            >
                              <img className="thum" src={formatImageName(image)} alt={`Thumbnail ${index}`} />
                            </div>
                          ))}
                        </Slider>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-xl-6">
                  <div className="product-details__content">
                    <span className="text-gray-700 mb-10">{data?.categoryId?.name}</span>
                    <h5 className="mb-12">{data?.name}</h5>
                    <div className="flex-align flex-wrap gap-12">
                      <div className="flex-align gap-12 flex-wrap">
                        <div className="flex-align gap-8">
                          <Rating initialRating={data?.rating} readonly />
                        </div>
                        <span className="text-sm fw-medium text-neutral-600">{t("starRating")}</span>
                        <span className="text-sm fw-medium text-gray-500">({data?.ratingCount})</span>
                      </div>
                      <span className="text-sm fw-medium text-gray-500">|</span>
                      <span className="text-gray-900">
                        <span className="text-gray-400">{t("sku")}:</span>
                        {data?.sku || t("notExists")}
                      </span>
                    </div>
                    <span className="mt-32 pt-32 text-gray-700 border-top border-gray-100 d-block" />
                    <p className="text-gray-700">
                      {data?.description?.length > 300
                        ? `${data?.description?.slice(0, 300)}...`
                        : data?.description}
                    </p>
                    <div className="my-32 flex-align gap-16 flex-wrap">
                      <div className="flex-align gap-8">
                        <h6 className="mb-0">
                          {getAcceptedOfferPrice(data.offers)
                            ? formatPrice(getAcceptedOfferPrice(data.offers), currency)
                            : data.discount > 0
                            ? formatPrice(calculateDiscountPrice(data.price, data.discount), currency)
                            : formatPrice(data.price, currency)}
                        </h6>
                      </div>
                      {!getAcceptedOfferPrice(data.offers) && data?.discount > 0 && (
                        <>
                          <div className="flex-align gap-8">
                            <h6 className="cutPrice text-lg text-gray-400 mb-0 fw-medium">
                              {formatPrice(data.price, currency)}
                            </h6>
                          </div>
                          <div className="flex-align gap-8">
                            <div className="flex-align gap-8 text-main-two-600">
                              <i className="ph-fill ph-seal-percent text-xl" />-{data.discount}%
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="">
                      <div className="flex-between align-items-start flex-wrap gap-16">
                        {data?.colors?.length > 0 && (
                          <div>
                            <span className="text-gray-900 d-block mb-12">
                              {t("color")}: <span className="fw-medium">{selected.color}</span>
                            </span>
                            <div className="color-list flex-align gap-8">
                              {data?.colors.map((color) => (
                                <button
                                  type="button"
                                  className="color-list__button w-20 h-20 border border-2 border-gray-50 rounded-circle "
                                  style={{ background: color.hex }}
                                  title={color.name}
                                  key={color._id}
                                  onClick={() => setSelected((prev) => ({ ...prev, color: color.name }))}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                        {data?.sizes?.length > 0 && (
                          <div>
                            <span className="text-gray-900 d-block mb-12">
                              {t("size")}: <span className="fw-medium">{selected.size}</span>
                            </span>
                            <div className="flex-align gap-8 flex-wrap">
                              {data.sizes.map((size) => (
                                <button
                                  to="#"
                                  onClick={() => setSelected((prev) => ({ ...prev, size: size }))}
                                  className="px-12 py-8 text-sm rounded-8 text-gray-900 border border-gray-200 hover-border-main-600 hover-text-main-600"
                                >
                                  {size}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <span className="mt-32 pt-32 text-gray-700 border-top border-gray-100 d-block" />

                    <div className="mb-32 ">
                      <form className="row align-items-end" onSubmit={submitOffer}>
                        <div className="col-md-8">
                          <div className="">
                            <label htmlFor="title" className="text-neutral-600 mb-8">
                              {t("createOffer")}
                            </label>
                            <input
                              type="number"
                              className="common-input rounded-8"
                              placeholder={t("enterPrice")}
                              step={"0.01"}
                              required
                              value={offer}
                              onChange={(e) => setOffer(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <button type="submit" className="btn btn-main py-18 w-100">
                            {t("submitOffer")}
                          </button>
                        </div>
                        {data.offers && data.offers.length > 0 && (
                          <div className="col-12 mt-10">
                            <span className="text-md fw-medium text-neutral-600">{t("myOffersList")}</span>
                            <ul className="customUl mt-2">
                              {data.offers.map((offer) => (
                                <li>
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span>{formatPrice(offer.price, currency)}</span>{" "}
                                    <span
                                      className={`${
                                        offer.status === "PENDING"
                                          ? "pendingBadge"
                                          : offer.status === "REJECTED"
                                          ? "rejectBadge"
                                          : "approvedBadge"
                                      }`}
                                    >
                                      {offer.status}
                                    </span>
                                    {offer.status === "COUNTERED" && (
                                      <>
                                        <span>{formatPrice(offer?.counterOffer?.price, currency)}</span>
                                        {offer.counterOffer.status === "PENDING" && (
                                          <span>
                                            <button
                                              type="button"
                                              onClick={() => acceptOffer(offer._id, "ACCEPTED")}
                                            >
                                              <Icon icon={"material-symbols:check-rounded"} />
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => acceptOffer(offer._id, "REJECTED")}
                                            >
                                              <Icon icon={"maki:cross"} />
                                            </button>
                                          </span>
                                        )}
                                        {offer.counterOffer.status !== "PENDING" && (
                                          <span
                                            className={`${
                                              offer.counterOffer.status === "PENDING"
                                                ? "pendingBadge"
                                                : offer.counterOffer.status === "REJECTED"
                                                ? "rejectBadge"
                                                : "approvedBadge"
                                            }`}
                                          >
                                            {offer.counterOffer.status}
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3">
              <div className="product-details__sidebar py-40 px-32 border border-gray-100 rounded-16">
                <div className="mb-32">
                  <span>{t("country")}</span>
                  <label htmlFor="delivery" className="h6 activePage mb-8 text-heading fw-semibold d-block">
                    {data?.shopId?.country?.name}
                  </label>
                </div>
                <div className="mb-32">
                  <span className="text-xl d-flex">
                    <i className="ph ph-location" />
                  </span>
                  <div className="d-flex rounded-4 overflow-hidden">
                    <button
                      onClick={decrementQuantity}
                      type="button"
                      className="quantity__minus flex-shrink-0 h-48 w-48 text-neutral-600 flex-center hover-bg-main-600 hover-text-white"
                    >
                      <i className="ph ph-minus" />
                    </button>
                    <input
                      type="number"
                      className="quantity__input flex-grow-1 border border-gray-100 border-start-0 border-end-0 text-center w-32 px-16"
                      id="stock"
                      value={selected.quantity}
                      readOnly
                    />
                    <button
                      onClick={incrementQuantity}
                      type="button"
                      className="quantity__plus flex-shrink-0 h-48 w-48 text-neutral-600 flex-center hover-bg-main-600 hover-text-white"
                    >
                      <i className="ph ph-plus" />
                    </button>
                  </div>
                </div>
                <div className="mb-32">
                  <div className="flex-between flex-wrap gap-8 border-bottom border-gray-100 pb-16 mb-16">
                    <span className="text-gray-500">{t("price")}</span>
                    <h6 className="text-lg mb-0">
                      {getAcceptedOfferPrice(data.offers)
                        ? formatPrice(getAcceptedOfferPrice(data.offers), currency)
                        : data.discount > 0
                        ? formatPrice(calculateDiscountPrice(data.price, data.discount), currency)
                        : formatPrice(data.price, currency)}
                    </h6>
                  </div>
                  <div className="flex-between flex-wrap gap-8">
                    <span className="text-gray-500">{t("totalAmount")}</span>
                    <h6 className="text-lg mb-0">
                      {getAcceptedOfferPrice(data.offers)
                        ? formatPrice(getAcceptedOfferPrice(data.offers) * selected.quantity, currency)
                        : data.discount > 0
                        ? formatPrice(
                            calculateDiscountPrice(data.price, data.discount) * selected.quantity,
                            currency
                          )
                        : formatPrice(data.price * selected.quantity, currency)}
                    </h6>
                  </div>
                </div>
                <button
                  onClick={addToCart}
                  className="btn btn-main w-100 flex-center gap-8 rounded-8 py-16 fw-normal mt-48"
                >
                  <i className="ph ph-shopping-cart-simple text-lg" />
                  {t("addToCart")}
                </button>
                <button
                  onClick={buyNow}
                  className="btn btn-outline-main rounded-8 py-16 fw-normal mt-16 w-100"
                >
                  {t("buyNow")}
                </button>
                <div className="mt-32">
                  <div className="storeCard px-12 py-24">
                    <div className="flex-align gap-8">
                      <span className="w-60 h-60 bg-main-600 rounded-circle flex-center text-2xl">
                        <i className="ph ph-storefront text-white text-[27px]" />
                      </span>
                      <div>
                        <h6 className="text-xl mb-0">{data?.shopId?.shopName}</h6>
                        <span className="">{t("verifiedShop")}</span>
                      </div>
                    </div>
                    <Link
                      to={`/shops/${data?.shopId?.slug}`}
                      className="mt-20 btn btn-main rounded w-100 text-uppercase"
                    >
                      {t("viewStore")}
                    </Link>
                    {token && (
                      <button
                        onClick={handleInitiateChat}
                        className="mt-20 btn btn-main rounded w-100 text-uppercase"
                      >
                        {t("chatNow")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="pt-80">
            <div className="product-dContent border rounded-24">
              <div className="product-dContent__header border-bottom border-gray-100 flex-between flex-wrap gap-16">
                <ul className="nav common-tab nav-pills mb-3" id="pills-tab" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link active"
                      id="pills-description-tab"
                      data-bs-toggle="pill"
                      data-bs-target="#pills-description"
                      type="button"
                      role="tab"
                      aria-controls="pills-description"
                      aria-selected="true"
                    >
                      {t("description")}
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="pills-reviews-tab"
                      data-bs-toggle="pill"
                      data-bs-target="#pills-reviews"
                      type="button"
                      role="tab"
                      aria-controls="pills-reviews"
                      aria-selected="false"
                    >
                      {t("reviews")}
                    </button>
                  </li>
                </ul>
                {/* <Link
                  to="#"
                  className="btn bg-color-one rounded-16 flex-align gap-8 text-main-600 hover-bg-main-600 hover-text-white"
                >
                  <img src="assets/images/icon/satisfaction-icon.png" alt="" />
                  {t("satisfactionGuaranteed")}
                </Link> */}
              </div>
              <div className="product-dContent__box">
                <div className="tab-content" id="pills-tabContent">
                  <div
                    className="tab-pane fade show active"
                    id="pills-description"
                    role="tabpanel"
                    aria-labelledby="pills-description-tab"
                    tabIndex={0}
                  >
                    <div dangerouslySetInnerHTML={{ __html: data.specification }} />
                  </div>
                  <div
                    className="tab-pane fade"
                    id="pills-reviews"
                    role="tabpanel"
                    aria-labelledby="pills-reviews-tab"
                    tabIndex={0}
                  >
                    <div className="row g-4">
                      <div className="col-lg-12">
                        <h6 className="mb-24">{t("customerFeedback")}</h6>
                        <div className="row">
                          <div className="col-md-4">
                            <div className="border border-gray-100 rounded-8 px-40 py-52 flex-center flex-column flex-shrink-0 text-center">
                              <h2 className="mb-6 text-main-600">{data.rating}</h2>
                              <div className="flex-center gap-8">
                                <Rating readonly initialRating={data?.rating} />
                              </div>
                              <span className="mt-16 text-gray-500">
                                {data?.ratingCount} {t("reviews", { count: data?.ratingCount })}
                              </span>
                            </div>
                          </div>
                          <div className="col-md-8">
                            <div className="border border-gray-100 rounded-8 px-24 py-40 flex-grow-1">
                              {data?.ratingsCount &&
                                Array.from({ length: 5 })?.map((_, index) => (
                                  <div className="row mb-10">
                                    <div className="col-10 d-flex align-items-center gap-8">
                                      <span className="text-gray-900 flex-shrink-0">{index + 1}</span>
                                      <div
                                        className="progress w-100 bg-gray-100 rounded-pill h-8"
                                        role="progressbar"
                                        aria-label="Basic example"
                                        aria-valuenow={70}
                                        aria-valuemin={0}
                                        aria-valuemax={100}
                                      >
                                        <div
                                          className="progress-bar bg-main-600 rounded-pill"
                                          style={{ width: `${(index + 1) * 20}%` }}
                                        />
                                      </div>
                                    </div>
                                    <div className="col-md-2 d-flex align-items-center gap-8">
                                      <div className="flex-align gap-4">
                                        <Rating readonly initialRating={index + 1} />
                                      </div>
                                      <span className="text-gray-900 flex-shrink-0">
                                        {data?.ratingsCount[parseInt(index + 1).toString()]}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-12">
                        <h6 className="mb-24">{t("reviews")}</h6>
                        {reviews.map((item) => (
                          <div
                            className="d-flex align-items-start gap-24 pb-44 border-bottom border-gray-100 mb-44"
                            key={item?._id}
                          >
                            <img
                              src="/assets/images/thumbs/comment-img1.png"
                              alt=""
                              className="w-52 h-52 object-fit-cover rounded-circle flex-shrink-0"
                            />
                            <div className="flex-grow-1">
                              <div className="flex-between align-items-start gap-8 ">
                                <div className="">
                                  <h6 className="mb-12 text-md">{item?.user?.name}</h6>
                                  <div className="flex-align gap-8">
                                    <Rating readonly initialRating={item?.rating} />
                                  </div>
                                </div>
                                <span className="text-gray-800 text-xs">{timeAgo(item?.createdAt)}</span>
                              </div>
                              <p className="text-gray-700">{item.review}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <NewArrivalTwo related={related} />
    </Loading>
  );
};

export default ProductDetailsTwo;
